import { useState } from "react";
import { api, ApiError } from "../../../lib/api-client";

interface BulkActionBarProps {
  selectedIds: string[];
  onClear: () => void;
  onComplete: () => void;
}

// `Release` is the only batch-level dispatch endpoint — for batch/hybrid
// JobTypes it moves every document in the batch to the export step and
// fires export jobs. Flow-mode batches 400 (they don't have a batch gate).
// Failures are surfaced per-batch so the user can act on them instead of
// having the error swallowed by a silent Promise.all.
export function BulkActionBar({ selectedIds, onClear, onComplete }: BulkActionBarProps) {
  const [loading, setLoading] = useState(false);
  const [failures, setFailures] = useState<{ batchId: string; message: string }[]>([]);

  if (selectedIds.length === 0) return null;

  async function handleRelease() {
    setLoading(true);
    setFailures([]);
    const results = await Promise.allSettled(
      selectedIds.map((id) => api.post(`/batches/${id}/release`).then(() => id)),
    );
    const failed = results.flatMap((r, i) => {
      if (r.status === "fulfilled") return [];
      const err = r.reason;
      const message =
        err instanceof ApiError && err.body && typeof err.body === "object"
          ? (err.body as { error?: string }).error ?? `HTTP ${err.status}`
          : err instanceof Error
            ? err.message
            : "Unknown error";
      return [{ batchId: selectedIds[i]!, message }];
    });
    setLoading(false);
    onComplete();
    if (failed.length === 0) {
      onClear();
    } else {
      setFailures(failed);
    }
  }

  return (
    <div className="bulk-action-bar" data-testid="bulk-action-bar">
      <span className="bulk-action-count">
        {selectedIds.length} batch{selectedIds.length !== 1 ? "es" : ""} selected
      </span>
      <button
        className="batch-action-btn batch-action-btn--primary"
        disabled={loading}
        onClick={handleRelease}
        title="Release batch-mode documents for export"
      >
        {loading ? "Releasing…" : "Release for Export"}
      </button>
      <button
        className="batch-action-btn batch-action-btn--secondary"
        disabled={loading}
        onClick={onClear}
      >
        Clear
      </button>
      {failures.length > 0 && (
        <div className="bulk-action-errors" role="alert">
          {failures.length} of {selectedIds.length} failed — {failures[0]!.message}
          {failures.length > 1 ? ` (+${failures.length - 1} more)` : ""}
        </div>
      )}
    </div>
  );
}
